import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { useI18n } from "../../i18n/LanguageContext";
import { Badge } from "../../components/ui/badge";
import { Download } from "lucide-react";
import { toast } from "sonner";

export default function SuperAdminOrders() {
  const { t } = useI18n();
  const [rows, setRows] = useState(null);
  const [status, setStatus] = useState("");

  useEffect(() => {
    setRows(null);
    api.saListOrders(status).then(setRows).catch((e) => { toast.error(e.message); setRows([]); });
  }, [status]);

  return (
    <div className="space-y-6" data-testid="sa-orders-page">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-[#F72585]">{t("superadmin.title")}</div>
          <h1 className="text-3xl md:text-4xl font-black tracking-tight">{t("superadmin.orders")}</h1>
        </div>
        <div className="flex gap-3 items-center">
          <select value={status} onChange={(e) => setStatus(e.target.value)}
                  className="h-10 rounded-md border border-black/10 px-3 font-semibold bg-white text-sm"
                  data-testid="sa-orders-status-filter">
            <option value="">{t("superadmin.all")}</option>
            <option value="pending">{t("superadmin.pending")}</option>
            <option value="succeeded">{t("superadmin.succeeded")}</option>
            <option value="cancelled">{t("superadmin.cancelled")}</option>
          </select>
          <a href={api.saExportUrl("orders")} target="_blank" rel="noreferrer"
             className="inline-flex items-center gap-2 rounded-full bg-white border border-black/10 px-4 py-2 text-sm font-bold hover:bg-black/5"
             data-testid="sa-export-orders-link">
            <Download className="h-4 w-4" /> {t("superadmin.exportOrders")}
          </a>
        </div>
      </header>

      <div className="rounded-2xl border border-black/5 bg-white overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-black/5">
            <tr>
              <Th>order_id</Th><Th>user_id</Th><Th>course_id</Th><Th>scope</Th>
              <Th>amount</Th><Th>status</Th><Th>provider</Th><Th>created</Th>
            </tr>
          </thead>
          <tbody>
            {rows === null && <tr><td colSpan={8} className="p-6 text-center text-black/50">{t("superadmin.loading")}</td></tr>}
            {rows?.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-black/50">{t("superadmin.empty")}</td></tr>}
            {rows?.map((o) => (
              <tr key={o.order_id} className="border-t border-black/5" data-testid={`sa-order-${o.order_id}`}>
                <Td className="font-mono text-xs">{o.order_id}</Td>
                <Td className="font-mono text-xs">{o.user_id}</Td>
                <Td className="font-mono text-xs">{o.course_id}</Td>
                <Td className="text-xs">{o.scope}</Td>
                <Td className="font-bold">{o.amount_display}</Td>
                <Td>
                  <Badge className={`text-[10px] ${
                    o.status === "succeeded" ? "bg-[#4CC9F0]/20 text-[#0A5566]" :
                    o.status === "pending" ? "bg-[#FFB703]/20 text-[#7A4B00]" :
                    "bg-black/10 text-black/60"
                  }`}>{o.status}</Badge>
                </Td>
                <Td className="text-xs">{o.provider}</Td>
                <Td className="text-xs text-black/60">{o.created_at?.slice(0, 16).replace("T", " ")}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Th({ children }) { return <th className="text-start px-3 py-2.5 text-[11px] font-black uppercase tracking-wider text-black/60">{children}</th>; }
function Td({ children, className = "" }) { return <td className={`px-3 py-2.5 ${className}`}>{children}</td>; }
